import { isTouch } from '../core/device';
import { controllerConnected } from '../core/Gamepads';
import { PAD_HELP } from '../core/padmap';
import { h } from './dom';

/**
 * How to play: the goal, the controls for whatever you're playing on, and tips. Shared by
 * the phone's Help tab and the start screen.
 */

const TOUCH_KEYS: Array<[string, string]> = [
  ['Left thumb', 'Joystick: walk (and drive)'],
  ['Right side', 'Drag to look around · pinch to zoom'],
  [':croc:', 'Crocs → SPORT MODE (sprint, tap to toggle)'],
  [':hop:', 'Jump · hold for the handbrake'],
  ['E', 'Interact / talk / use (or tap the prompt)'],
  [':beer:', 'Crack a cold one (:air-horn: honks while driving)'],
  [':hotdog:', 'Eat'],
  ['Hotbar', 'Tap an item to use it (Mtn Dewd, scratchers, fireworks)'],
  [':phone: :banjo: ⛶', 'Phone · music · fullscreen'],
  [':rascal:', "Lil' Rascal (hotbar): hop on or find it · :hop: hops off"],
  [':globe:', 'Neighbors Online: invite friends, chat (Phone → Online)'],
];

const KEYS: Array<[string, string]> = [
  ['WASD / ↑↓←→', 'Walk'],
  ['Shift', 'Crocs → SPORT MODE (sprint)'],
  ['Space', 'Jump / stunt timing'],
  ['E', 'Interact / talk / use'],
  ['Q', 'Crack a cold one'],
  ['F', 'Eat'],
  ['R', 'Chug a Mtn Dewd'],
  ['T', 'Scratch a ticket'],
  ['G', 'Light fireworks'],
  ['P / Tab', 'Phone'],
  ['M', 'Music on/off'],
  ['Drag mouse', 'Look around · Wheel to zoom'],
  ['H', 'Honk (while driving) · meep (on the Rascal)'],
  ['B', "Lil' Rascal: hop on (or find where you left it) · Space hops off"],
  ['C', 'Chat with neighbors (Phone → :globe: Online to invite friends)'],
];

const TIPS = [
  "Cans and scrap sparkle on the ground. Walk over them. Sell them at Junkyard Jerry's (north end of the park).",
  'Every beer you drink leaves a can. The circle of life.',
  "A good buzz (25-84) gives +50% Clout. Too much and you'll be seeing double. 100 = blackout.",
  'The job board is at Suds-N-Duds across the highway. Mowing pays by how much grass you cut.',
  'Fix the Camaro with parts from Jerry. When she runs, drive her (E) or sell her for $1,200.',
  "Buy yard decor at Lucky's Pawn. It raises your Redneck Rating, and some of it does stuff.",
  'Walk in your front door: sleep in your bed after 8 PM, eat out of the fridge, watch TV on the couch. Stay up past 3 AM and you WILL wake up somewhere weird.',
  "Furniture and wall decor for inside your place are at Lucky's Pawn. Use the spot by your front door (inside) to change the paint, the carpet, and what's out on display.",
  'Watch the highway. Traffic does not care about your Crocs, and the 18-wheelers care even less.',
  "After the double wide: chip in at the park office to buy Lucky Acres back from Apex Capital. In a multiplayer park, everybody's jar money counts.",
  "South of the highway, past the Gas-N-Grub: the Booze Barn (cheap beer, liquor, lotto) and the Rusty Spur bar (walk in for darts, video poker, arm wrestling and the mechanical bull). Cooter's Used Cars is past Lucky's Pawn.",
  'Cooter sells lifted trucks, donks, an El Camino and worse. Whatever you buy lands in your driveway; swap rides at his office.',
  "Fairgrounds Road (east of the park): the rodeo (ride Widowmaker 4 to 11 PM for a $150 purse), the mud bog (pay at the sign, out and back, big tires help) and Bubba's Gun Range (cans only).",
  'Mega Bucks: pick 5 numbers at the Gas-N-Grub or the Booze Barn. Drawing every Saturday at 8 PM; everybody gets the same numbers.',
  "Multiplayer: walk up to a neighbor and press E to hang out. Arm wrestle or shoot dice for money. The Online tab keeps the park's records.",
  "The Back Forty (north end of Possum Trot Lane) is where the side money is: noodle catfish in the crick (Big Earl buys them), strip copper out of the condemned trailers (Junkyard Jerry pays by the pound) and run Uncle Rudy's still with Sweet Feed from the Gas-N-Grub.",
  "Trenchcoat Terry buys moonshine at the burn barrels after dark. Lil' Ricky plays cornhole for money at the Possum Trot Commons.",
  'V (or scrolling all the way in) switches to first person. L turns on a flashlight or headlamp if you bought one at the Gas-N-Grub.',
  'Walk into the shops: you buy through the glass, the bars or the chicken wire. Inside, a little badge floats over everything you can use, and the thing E would use glows yellow.',
  "Suds-N-Duds: wash a load and you're fresh all day (+1 Clout on everything). The lost and found swaps your clothes. The dryers sometimes have change in them.",
  'North end of Main Street: Brenda (Lot 12) cuts hair, Madame Wanda (Lot 14) reads tarot (the card changes your day), Hank (Lot 15) sells tinfoil hats and listens to the ham radio.',
  'Fire pits light up after 6 PM: sit, roast a hot dog, hear a story. The curb couch by the BMX field burns real good after dark if you have a lighter.',
  "Gus pays the day's top shot at the gun range: 15 points. The running possum and the clays are worth 3, Bigfoot is worth 5. Hold your breath (Shift) to steady up.",
  "Cooter sells a Lil' Rascal mobility scooter ($350). Ride it anywhere you can walk, stores and the bar included: faster than Sport Mode, turbo with Shift, and it charges parked at home.",
  ":briefcase: Hustles (on this phone) lists every way to make money. The quick ones (favors, the driveway car wash) unlock Raylene's delivery runs, and those unlock the big schemes.",
  'A :dollar: over a neighbor means they want a favor: a cold one, their lost teeth, a cooler moved, a casserole run across the park. Talk to them. Eight a day.',
  'Hang the CAR WASH sign by your mailbox (9 AM to 7 PM): customers pull up in the street, you scrub off every splatter of mud. Fast work gets a tip.',
  'Delivery runs: the food goes cold in about two minutes, and so does the tip. A car or the Rascal helps.',
  "Shine runs: Deputy Boyd only patrols the park roads (Main Street, North Road, Possum Trot). Cut through yards, stay out of his spotlight, and the Back Forty's past his beat.",
  'Lot 10 (across from Jenkins) can be yours: lease it from Darlene, haul the wreck to Jerry, call Big Dave from the sign for a trailer, fix it up, then rent it out for $120 every Friday or flip it.',
];

const DREAM =
  "Save $2,500 for a DOUBLE WIDE from Big Dave's (by the park entrance). Pay your lot rent every Friday at 5 PM or take a strike. Three strikes and you're evicted. Rent goes up every week once the private equity guys buy the park.";

/** `dream` adds the goal up top; `tips` caps how many tips follow (all by default). */
export function controlsHelp(opts: { dream?: boolean; tips?: number } = {}): HTMLElement[] {
  const out: HTMLElement[] = [];
  if (opts.dream !== false)
    out.push(
      h('h3', { text: ':target: The Dream' }),
      h('div', { style: { fontSize: '14px', lineHeight: '1.5' }, text: DREAM }),
    );
  out.push(h('h3', { text: isTouch() ? ':point-up: Controls' : ':keyboard: Controls' }));
  if (isTouch())
    out.push(
      h('div', {
        style: { fontSize: '13px', margin: '0 0 8px', color: '#606770' },
        text: 'Tip: "Add to Home Screen" from your browser menu to play fullscreen like an app.',
      }),
    );
  const grid = h('div', { class: 'help-grid' });
  for (const [k, v] of isTouch() ? TOUCH_KEYS : KEYS)
    grid.append(h('span', { class: 'keycap', text: k }), h('span', { text: v }));
  out.push(grid);
  if (controllerConnected()) {
    const pad = h('div', { class: 'help-grid' });
    for (const [k, v] of PAD_HELP)
      pad.append(h('span', { class: 'keycap', text: k }), h('span', { text: v }));
    out.push(h('h3', { text: ':gamepad: Controller' }), pad);
  }
  const tips = TIPS.slice(0, opts.tips ?? TIPS.length);
  if (tips.length) {
    out.push(h('h3', { text: ':bulb: Tips' }));
    for (const t of tips)
      out.push(h('div', { style: { fontSize: '14px', margin: '6px 0' }, text: `• ${t}` }));
  }
  return out;
}
